import { useState } from 'react'
import { useApp } from '../context/AppContext'
import { persistAppendOnlyVersion, persistDeletedVersion } from '../lib/lumiAppendOnly'
import { formatDate } from '../utils'
import type { Fornecedor } from '../types'

function nowISO() {
  return new Date().toISOString()
}

const STATUS_COLOR: Record<string, string> = {
  ATIVO: '#4ade80', INATIVO: '#facc15', BLOQUEADO: '#f87171',
}

const empty: Omit<Fornecedor, 'id' | 'createdAt' | '_id'> = {
  razao_social: '', nome_fantasia: '', cnpj_cpf: '', email: '', telefone: '',
  contato_principal: '',
  endereco_logradouro: '',
  endereco_numero: '',
  endereco_complemento: '',
  endereco_bairro: '',
  endereco_cidade: '',
  endereco_estado: '',
  endereco_cep: '',
  status: 'ATIVO',
}

export default function Fornecedores() {
  const { fornecedores, setFornecedores, addLog } = useApp()
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState<Fornecedor | null>(null)
  const [form, setForm] = useState({ ...empty })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [toast, setToast] = useState('')
  const [saving, setSaving] = useState(false)

  const filtered = fornecedores.filter(f =>
    f.razao_social.toLowerCase().includes(search.toLowerCase()) ||
    f.nome_fantasia.toLowerCase().includes(search.toLowerCase()) ||
    f.cnpj_cpf.includes(search)
  )

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  function openNew() { setEditing(null); setForm({ ...empty }); setErrors({}); setModal(true) }

  function openEdit(f: Fornecedor) {
    setEditing(f)
    setForm({
      razao_social: f.razao_social,
      nome_fantasia: f.nome_fantasia,
      cnpj_cpf: f.cnpj_cpf,
      email: f.email,
      telefone: f.telefone,
      contato_principal: f.contato_principal,
      endereco_logradouro: f.endereco_logradouro || '',
      endereco_numero: f.endereco_numero || '',
      endereco_complemento: f.endereco_complemento || '',
      endereco_bairro: f.endereco_bairro || '',
      endereco_cidade: f.endereco_cidade || '',
      endereco_estado: f.endereco_estado || '',
      endereco_cep: f.endereco_cep || '',
      status: f.status,
    })
    setErrors({})
    setModal(true)
  }

  function validate() {
    const e: Record<string, string> = {}
    if (!form.razao_social.trim()) e.razao_social = 'Razão social obrigatória'
    if (!form.cnpj_cpf.trim()) e.cnpj_cpf = 'CNPJ/CPF obrigatório'
    return e
  }

  async function save() {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }
    setSaving(true)
    try {
      if (editing) {
        const dbId = editing._id ?? editing.id
        const atualizado = { ...editing, ...form, updatedAt: nowISO() } as Fornecedor
        await persistAppendOnlyVersion('fornecedores', atualizado as unknown as Record<string, unknown>)
        setFornecedores(prev => prev.map(f => ((f as any)._id ?? f.id) === dbId ? atualizado : f))
        addLog('FORNECEDOR', dbId, 'EDITADO', `Fornecedor ${form.razao_social} editado`)
        showToast('Fornecedor atualizado')
      } else {
        const novo: Omit<Fornecedor, '_id'> = { id: 'f' + Date.now(), ...form, createdAt: nowISO() }
        await persistAppendOnlyVersion('fornecedores', novo as unknown as Record<string, unknown>)
        setFornecedores(prev => [...prev, novo])
        addLog('FORNECEDOR', novo.id, 'CRIADO', `Fornecedor ${form.razao_social} criado`)
        showToast('Fornecedor cadastrado')
      }
      setModal(false)
    } catch { showToast('Erro ao salvar. Tente novamente.') } finally { setSaving(false) }
  }

  async function toggleStatus(f: Fornecedor) {
    const ns = f.status === 'ATIVO' ? 'INATIVO' : 'ATIVO'
    const dbId = f._id ?? f.id
    try {
      const atualizado = { ...f, status: ns, updatedAt: nowISO() } as Fornecedor
      await persistAppendOnlyVersion('fornecedores', atualizado as unknown as Record<string, unknown>)
      setFornecedores(prev => prev.map(x => ((x as any)._id ?? x.id) === dbId ? atualizado : x))
      showToast(`Fornecedor ${ns === 'ATIVO' ? 'ativado' : 'inativado'}`)
    } catch { showToast('Erro ao alterar status.') }
  }

  async function deleteFornecedor(f: Fornecedor) {
    const confirmed = window.confirm(`Excluir o fornecedor ${f.razao_social}? Esta acao nao pode ser desfeita.`)
    if (!confirmed) return

    setSaving(true)
    try {
      const dbId = f._id ?? f.id
      await persistDeletedVersion('fornecedores', f as unknown as Record<string, unknown>)
      setFornecedores(prev => prev.filter(item => ((item as any)._id ?? item.id) !== dbId))
      addLog('FORNECEDOR', dbId, 'CANCELADO', `Fornecedor ${f.razao_social} excluido`)
      showToast('Fornecedor excluido')
    } catch {
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Fornecedores</h1>
          <p style={{ color: '#62666d', fontSize: 13, margin: '2px 0 0' }}>{fornecedores.length} fornecedor{fornecedores.length !== 1 ? 'es' : ''} cadastrado{fornecedores.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openNew} style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
          + Novo Fornecedor
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nome ou CNPJ..."
          style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 12px', color: '#f7f8f8', fontSize: 13, width: 300, outline: 'none' }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏢</div>
          <p style={{ fontSize: 14 }}>Nenhum fornecedor encontrado</p>
          <button onClick={openNew} style={{ marginTop: 12, background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 16px', color: '#ffb4b1', fontSize: 13, cursor: 'pointer' }}>Cadastrar Fornecedor</button>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {['Razão Social', 'Nome Fantasia', 'CNPJ/CPF', 'Contato', 'Status', 'Cadastrado', 'Ações'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(f => (
              <tr key={f._id ?? f.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{f.razao_social}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{f.nome_fantasia}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab', fontFamily: 'monospace' }}>{f.cnpj_cpf}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{f.contato_principal}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: STATUS_COLOR[f.status] + '22', color: STATUS_COLOR[f.status], borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{f.status}</span>
                </td>
                <td style={{ padding: '10px 12px', color: '#62666d' }}>{formatDate(f.createdAt)}</td>
                <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(f)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer' }}>Editar</button>
                  <button onClick={() => toggleStatus(f)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#62666d', fontSize: 12, cursor: 'pointer' }}>{f.status === 'ATIVO' ? 'Inativar' : 'Ativar'}</button>
                  <button onClick={() => deleteFornecedor(f)} disabled={saving} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 28, width: 520, maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editing ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { label: 'Razão Social *', key: 'razao_social', full: true },
                { label: 'Nome Fantasia', key: 'nome_fantasia' },
                { label: 'CNPJ/CPF *', key: 'cnpj_cpf' },
                { label: 'Email', key: 'email' },
                { label: 'Telefone', key: 'telefone' },
                { label: 'Contato Principal', key: 'contato_principal' },
                { label: 'CEP', key: 'endereco_cep' },
                { label: 'Logradouro', key: 'endereco_logradouro', full: true },
                { label: 'Numero', key: 'endereco_numero' },
                { label: 'Complemento', key: 'endereco_complemento' },
                { label: 'Bairro', key: 'endereco_bairro' },
                { label: 'Cidade', key: 'endereco_cidade' },
                { label: 'UF', key: 'endereco_estado' },
              ].map(({ label, key, full }) => {
                const fieldId = `fornecedor-${key}`
                return (
                <div key={key} style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
                  <label htmlFor={fieldId} style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>{label}</label>
                  <input
                    id={fieldId}
                    type={key === 'email' ? 'email' : 'text'}
                    value={(form as any)[key]}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    style={{ width: '100%', background: '#1c1d24', border: `1px solid ${errors[key] ? '#f87171' : 'rgba(255,255,255,0.08)'}`, borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                  {errors[key] && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors[key]}</p>}
                </div>
                )
              })}
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Status</label>
                <select
                  value={form.status}
                  onChange={e => setForm(p => ({ ...p, status: e.target.value as any }))}
                  style={{ width: '100%', background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none' }}
                >
                  <option value="ATIVO">ATIVO</option>
                  <option value="INATIVO">INATIVO</option>
                  <option value="BLOQUEADO">BLOQUEADO</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(false)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={save} disabled={saving} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>{saving ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
